import React, { useState } from 'react';
import { Building2, Loader2, Plus, X } from 'lucide-react';
import type { GbuAnlass, SifaArbeitsbereich } from '../../../services/sifa/gbuTypes';
import { ANLAESSE, ANLASS_LABEL, aktiveBereiche, bereichName, pruefeNeueGbu } from './gbuModulLogic';

interface Props {
  bereiche: SifaArbeitsbereich[];
  laeuft: boolean;
  onClose: () => void;
  onAnlegen: (eingabe: { bereichId: string; titel: string; anlass: GbuAnlass }) => void;
  /** Ohne Arbeitsbereich: zuerst in „Betrieb" anlegen. */
  onZuBetrieb: () => void;
}

const FELD = 'w-full px-3 py-2 min-h-[44px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-amber-300 disabled:text-slate-500';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';

/**
 * „Neue GBU": Arbeitsbereich waehlen (Pflicht, Plan §4.3), Titel (Vorgabe =
 * Bezeichnung des Bereichs), Anlass. Ohne aktiven Bereich steht statt des
 * Formulars der Weg nach „Betrieb".
 */
export const NeueGbuDialog: React.FC<Props> = ({ bereiche, laeuft, onClose, onAnlegen, onZuBetrieb }) => {
  const auswahl = aktiveBereiche(bereiche);
  const [bereichId, setBereichId] = useState(auswahl[0]?.id ?? '');
  const [titel, setTitel] = useState(auswahl[0]?.bezeichnung ?? '');
  /** Hat der Nutzer den Titel selbst geschrieben, folgt er dem Bereich nicht mehr. */
  const [titelEigen, setTitelEigen] = useState(false);
  const [anlass, setAnlass] = useState<GbuAnlass>('ersterstellung');
  const [fehler, setFehler] = useState<string[]>([]);

  const bereichWaehlen = (id: string) => {
    setBereichId(id);
    if (!titelEigen) setTitel(auswahl.find((b) => b.id === id)?.bezeichnung ?? '');
  };

  const anlegen = () => {
    const probleme = pruefeNeueGbu({ bereichId, titel });
    if (probleme.length > 0) { setFehler(probleme); return; }
    onAnlegen({ bereichId, titel: titel.trim(), anlass });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={laeuft ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <h3 className="text-sm font-bold text-slate-800">Neue Gefährdungsbeurteilung</h3>
          <button onClick={onClose} disabled={laeuft} className="p-2 hover:bg-slate-100 rounded-full disabled:opacity-40"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        {auswahl.length === 0 ? (
          <div className="p-4 space-y-3">
            <p className="text-sm text-slate-700">
              Eine Gefährdungsbeurteilung gehört immer zu einem Arbeitsbereich bzw. einer Tätigkeit
              (z. B. „Einschalen"). Im Betrieb ist noch kein aktiver Arbeitsbereich angelegt.
            </p>
            <button onClick={onZuBetrieb} className="w-full min-h-[44px] flex items-center justify-center gap-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl">
              <Building2 className="w-4 h-4" /> Zuerst in „Betrieb" anlegen
            </button>
          </div>
        ) : (
          <>
            <div className="p-4 space-y-3 overflow-y-auto">
              <div>
                <label className={LABEL}>Arbeitsbereich</label>
                <select value={bereichId} onChange={(e) => bereichWaehlen(e.target.value)} disabled={laeuft} className={FELD}>
                  {auswahl.map((b) => <option key={b.id} value={b.id}>{bereichName(auswahl, b.id)}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL}>Titel</label>
                <input value={titel} onChange={(e) => { setTitel(e.target.value); setTitelEigen(true); }} disabled={laeuft} className={FELD} maxLength={160} />
              </div>
              <div>
                <label className={LABEL}>Anlass</label>
                <select value={anlass} onChange={(e) => setAnlass(e.target.value as GbuAnlass)} disabled={laeuft} className={FELD}>
                  {ANLAESSE.map((a) => <option key={a} value={a}>{ANLASS_LABEL[a]}</option>)}
                </select>
              </div>
              <p className="text-[11px] text-slate-500">
                „Erstellt" ist vorbelegt mit Ihrem Namen, „Freigegeben" mit dem Ansprechpartner des Projekts — beides lässt sich im Editor ändern.
              </p>
              {fehler.length > 0 && <ul className="text-xs text-red-700 space-y-0.5">{fehler.map((f) => <li key={f}>{f}</li>)}</ul>}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2 shrink-0">
              <button onClick={onClose} disabled={laeuft} className="min-h-[44px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl disabled:opacity-40">Abbrechen</button>
              <button onClick={anlegen} disabled={laeuft} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl disabled:opacity-40">
                {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Anlegen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
